import type { FormikErrors } from 'formik'
import type { BillingPlan } from '@/lib/billing/types'
import { validatePlanFields } from '@/lib/billing/utils/plan-validation'
import { validateSystemFreePlanCreate } from '@/lib/billing/default-free-plan'
import { DEFAULT_PLAN_FORM } from './defaults'
import { NUMERIC_FIELDS, type NumericField, type NumericKey, type SectionId } from './catalog'
import type { PlanFormData, PlanPayload } from './types'
import { englishPlanPresentation, type PlanPresentation } from './presentation'

// Keep numeric drafts in Formik so invalid edits survive unmounting a section.
export type PlanEditorValues = Omit<PlanFormData, NumericKey> & Record<NumericKey, string | null>
export const NUMERIC_DEFINITIONS: NumericField[] = Object.values(NUMERIC_FIELDS)

export function createEditorValues(plan: BillingPlan | null): PlanEditorValues {
  const source = plan ?? DEFAULT_PLAN_FORM
  const values: Record<string, unknown> = { ...pickPlanFormData(source) }
  values.benefits = Array.isArray(source.benefits) ? [...source.benefits] : []
  if (values.voice_included) values.voice_trial_enabled = false
  for (const field of NUMERIC_DEFINITIONS) {
    const amount = source[field.key]
    values[field.key] = amount === null ? null : toEditorAmount(amount, field)
  }
  return values as PlanEditorValues
}

/** The fields the editor owns (not `features` or `billing_intervals`). */
export function pickPlanFormData(plan: PlanFormData | BillingPlan): PlanFormData {
  return Object.fromEntries(Object.keys(DEFAULT_PLAN_FORM).map(key => [key, plan[key as keyof PlanFormData]])) as unknown as PlanFormData
}

export function toEditorAmount(amount: number, field: NumericField): string {
  return String(amount / (field.scale ?? 1))
}

export function validateEditor(values: PlanEditorValues, presentation: PlanPresentation = englishPlanPresentation): FormikErrors<PlanEditorValues> {
  const { t } = presentation
  const errors: FormikErrors<PlanEditorValues> = {}
  if (!values.name.trim()) errors.name = t('internalIdentifier')
  if (!values.display_name.trim()) errors.display_name = t('planName')
  for (const field of NUMERIC_DEFINITIONS) {
    const value = values[field.key]
    if (value === null && field.empty) continue
    const parsed = parseAmount(value, field)
    if (parsed === null) {
      errors[field.key] = t('invalidNumber', { field: Object.values(presentation.fields).find(item => item.key === field.key)?.label.toLocaleLowerCase(presentation.locale) ?? field.label })
      continue
    }
    const problem = validatePlanFields({ [field.key]: parsed })
    if (problem) errors[field.key] = presentation.locale === 'en' ? problem : t('validField')
  }
  const problem = validateSystemFreePlanCreate({
    ...values,
    price_monthly_cents: parseAmount(values.price_monthly_cents, NUMERIC_FIELDS.price),
  })
  if (problem) errors.status = presentation.locale === 'en' ? problem : t('validField')
  return errors
}

export function serializeEditor(values: PlanEditorValues): PlanPayload {
  if (Object.keys(validateEditor(values)).length) throw new Error('Correct the highlighted fields before saving.')
  const { stripe_price_id: _price, stripe_product_id: _product, stripe_sync_status: _status, stripe_sync_error: _error, stripe_synced_at: _synced, ...editable } = values
  const result: Record<string, unknown> = { ...editable }
  for (const field of NUMERIC_DEFINITIONS) result[field.key] = parseAmount(values[field.key], field)
  result.shopify_plan_handle = values.shopify_plan_handle?.trim() || null
  return result as unknown as PlanPayload
}

export function fieldSection(key: string): SectionId {
  const numeric = NUMERIC_DEFINITIONS.find(field => field.key === key)
  if (numeric) return numeric.section
  switch (key) {
    case 'name': case 'display_name': case 'status': case 'benefits':
      return 'overview'
    case 'billing_channel': case 'billing_intervals':
    case 'shopify_plan_handle': case 'stripe_price_id': case 'stripe_product_id':
    case 'stripe_sync_status': case 'stripe_sync_error': case 'stripe_synced_at':
      return 'pricing'
    case 'plan_credits_expire':
      return 'credits'
    case 'voice_included': case 'voice_trial_enabled': case 'campaigns_enabled': case 'campaigns_trial_enabled':
      return 'voice'
    default:
      return 'overview'
  }
}

export function parseAmount(value: string | null, field: NumericField): number | null {
  if (value === null || value.trim() === '') return null
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(value.trim())) return null
  const amount = Number(value)
  if (!Number.isFinite(amount)) return null
  const scale = field.scale ?? 1
  if (field.step && field.step < 1 && scale === 1) return amount
  const decimals = Math.log10(scale)
  const [whole, fraction = ''] = value.trim().split('.')
  if (fraction.slice(decimals).replace(/0/g, '') !== '') return null
  const scaled = Number(whole + fraction.slice(0, decimals).padEnd(decimals, '0'))
  return Number.isSafeInteger(scaled) ? scaled : null
}
